import { emptyArray } from '@/store/emptyValues';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { DeviceAccessState, DeviceInfo, User } from '@/types/api';
import { apiClient } from '@/services/apiClient';

const initialState: DeviceAccessState = {
  loading: false,
  error: null,
  userDevices: {},
  deviceUsers: {},
  allDevices: []
};

// Async thunk to fetch all devices for admin users
export const fetchAllDevicesAsync = createAsyncThunk(
  'deviceAccess/fetchAllDevices',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get('/devices/');
      return response || [];
    } catch (error: any) {
      console.log('DEBUG: fetchAllDevicesAsync - Error:', error);
      console.log('DEBUG: fetchAllDevicesAsync - Error response:', error.response?.data);
      return rejectWithValue(error.response?.data?.detail || error.message || 'Failed to fetch all devices');
    }
  }
);

// Async thunks
export const fetchUserDevicesAsync = createAsyncThunk(
  'deviceAccess/fetchUserDevices',
  async (userId: number, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(`/devices/users/${userId}/devices`);
      console.log('DEBUG: fetchUserDevicesAsync response:', response);
      return { userId, devices: response.devices || [] };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || error.message || 'Failed to fetch user devices');
    }
  }
);

export const fetchDeviceUsersAsync = createAsyncThunk(
  'deviceAccess/fetchDeviceUsers',
  async (devEui: string, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(`/devices/${devEui}/users`);
      return { devEui, users: response.users };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || error.message || 'Failed to fetch device users');
    }
  }
);

export const addUserToDeviceAsync = createAsyncThunk(
  'deviceAccess/addUserToDevice',
  async ({ devEui, userId }: { devEui: string; userId: number }, { rejectWithValue }) => {
    console.log('DEBUG: frontend addUserToDeviceAsync called with', { devEui, userId });
    try {
      const payload = { user_id: userId };
      console.log('DEBUG: Sending payload to API:', payload);
      const response = await apiClient.post(`/devices/${devEui}/users`, payload);
      console.log('DEBUG: API response:', response);
      return { devEui, userId, message: response.message };
    } catch (error: any) {
      console.log('DEBUG: API error:', error);
      console.log('DEBUG: Error response data:', error.response?.data);
      console.log('DEBUG: Error response status:', error.response?.status);
      return rejectWithValue(error.response?.data?.detail || error.message || 'Failed to add user to device');
    }
  }
);

export const removeUserFromDeviceAsync = createAsyncThunk(
  'deviceAccess/removeUserFromDevice',
  async ({ devEui, userId }: { devEui: string; userId: number }, { rejectWithValue }) => {
    try {
      const response = await apiClient.delete(`/devices/${devEui}/users/${userId}`);
      return { devEui, userId, message: response.message };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || error.message || 'Failed to remove user from device');
    }
  }
);

const deviceAccessSlice = createSlice({
  name: 'deviceAccess',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearUserDevices: (state, action) => {
      const userId = action.payload;
      delete state.userDevices[userId];
    },
    clearDeviceUsers: (state, action) => {
      const devEui = action.payload;
      delete state.deviceUsers[devEui];
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch all devices
      .addCase(fetchAllDevicesAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllDevicesAsync.fulfilled, (state, action) => {
        state.loading = false;
        console.log('deviceAccessSlice: fetchAllDevicesAsync.fulfilled', {
          devicesLength: action.payload.length,
          previousAllDevicesLength: state.allDevices.length
        });
        state.allDevices = action.payload;
      })
      .addCase(fetchAllDevicesAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        console.log('deviceAccessSlice: fetchAllDevicesAsync.rejected', {
          error: action.payload
        });
      })
      // Fetch user devices
      .addCase(fetchUserDevicesAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserDevicesAsync.fulfilled, (state, action) => {
        state.loading = false;
        const { userId, devices } = action.payload;
        console.log('deviceAccessSlice: fetchUserDevicesAsync.fulfilled', {
          userId,
          devicesLength: devices.length,
          previousDevicesLength: state.userDevices[userId]?.length || 0
        });
        state.userDevices[userId] = devices;
      })
      .addCase(fetchUserDevicesAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch device users
      .addCase(fetchDeviceUsersAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDeviceUsersAsync.fulfilled, (state, action) => {
        state.loading = false;
        const { devEui, users } = action.payload;
        state.deviceUsers[devEui] = users;
      })
      .addCase(fetchDeviceUsersAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Add user to device
      .addCase(addUserToDeviceAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addUserToDeviceAsync.fulfilled, (state, action) => {
        state.loading = false;
        const { devEui, userId } = action.payload;
        
        console.log('deviceAccessSlice: addUserToDeviceAsync.fulfilled', {
          devEui,
          userId,
          currentUserDevicesKeys: Object.keys(state.userDevices),
          hasUserDevices: !!state.userDevices[userId]
        });
        
        // Update device users if cached - remove to trigger refetch
        if (state.deviceUsers[devEui]) {
          delete state.deviceUsers[devEui];
        }
        
        // Update user devices if cached - remove to trigger refetch
        if (state.userDevices[userId]) {
          console.log('deviceAccessSlice: Deleting user devices for userId', userId);
          delete state.userDevices[userId];
        }
      })
      .addCase(addUserToDeviceAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Remove user from device
      .addCase(removeUserFromDeviceAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removeUserFromDeviceAsync.fulfilled, (state, action) => {
        state.loading = false;
        const { devEui, userId } = action.payload;
        
        // Update device users if cached
        if (state.deviceUsers[devEui]) {
          state.deviceUsers[devEui] = state.deviceUsers[devEui].filter(user => user.id !== userId);
        }
        
        // Update user devices if cached
        if (state.userDevices[userId]) {
          state.userDevices[userId] = state.userDevices[userId].filter(device => device.dev_eui !== devEui);
        }
      })
      .addCase(removeUserFromDeviceAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, clearUserDevices, clearDeviceUsers } = deviceAccessSlice.actions;

// Selectors
export const selectDeviceAccess = (state: { deviceAccess: DeviceAccessState }) => state.deviceAccess;
export const selectUserDevices = (userId: number) => (state: { deviceAccess: DeviceAccessState }) => 
  state.deviceAccess.userDevices[userId] || emptyArray<DeviceInfo>();
export const selectDeviceUsers = (devEui: string) => (state: { deviceAccess: DeviceAccessState }) => 
  state.deviceAccess.deviceUsers[devEui] || emptyArray<User>();
export const selectAllDevices = (state: { deviceAccess: DeviceAccessState }) => 
  state.deviceAccess.allDevices;
export const selectDeviceAccessLoading = (state: { deviceAccess: DeviceAccessState }) => 
  state.deviceAccess.loading;
export const selectDeviceAccessError = (state: { deviceAccess: DeviceAccessState }) => 
  state.deviceAccess.error;

export default deviceAccessSlice.reducer;